import Chart from 'chart.js/auto';
import { CompareContext } from './result-compare.types';
import {
  getArchiveAccuracy,
  getClassAgreementRate,
  getMostProblematicFields,
  getNonReferenceIndices,
  getQualityDistribution,
  getSimilarityColor,
  truncateMiddle,
} from './result-compare.stats';

function chartDefaults() {
  const root = getComputedStyle(document.documentElement);
  return {
    textSecondary: root.getPropertyValue('--text-secondary').trim() || '#888',
    borderColor: root.getPropertyValue('--border-color').trim() || '#333',
  };
}

export function renderTokensChart(canvas: HTMLCanvasElement | undefined, ctx: CompareContext): Chart | null {
  if (!canvas) return null;
  const canvasCtx = canvas.getContext('2d');
  if (!canvasCtx) return null;
  const { textSecondary, borderColor } = chartDefaults();

  return new Chart(canvasCtx, {
    type: 'bar',
    data: {
      labels: [
        ctx.translate('RESULT.TOKENS_INPUT'),
        ctx.translate('RESULT.TOKENS_OUTPUT'),
        ctx.translate('RESULT.TOKENS_TOTAL'),
      ],
      datasets: ctx.dataList.map((d, i) => ({
        label: ctx.labelFor(i),
        data: [d.token_usage?.total?.input ?? 0, d.token_usage?.total?.output ?? 0, d.token_usage?.total?.total ?? 0],
        backgroundColor: ctx.colorFor(i).bg,
        borderColor: ctx.colorFor(i).border,
        borderWidth: 1,
        borderRadius: 6,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { labels: { color: textSecondary } } },
      scales: {
        x: { ticks: { color: textSecondary }, grid: { color: borderColor } },
        y: { ticks: { color: textSecondary }, grid: { color: borderColor }, beginAtZero: true },
      },
    },
  });
}

export function renderMetricsChart(canvas: HTMLCanvasElement | undefined, ctx: CompareContext, classificationMode: boolean): Chart | null {
  if (!canvas) return null;
  const canvasCtx = canvas.getContext('2d');
  if (!canvasCtx) return null;
  const { textSecondary, borderColor } = chartDefaults();

  const labels = classificationMode
    ? [ctx.translate('COMPARE.METRICS.AVG_CONFIDENCE') + ' (%)']
    : [
        ctx.translate('COMPARE.METRICS.TOTAL_FIELDS'),
        ctx.translate('COMPARE.METRICS.AVG_CONFIDENCE') + ' (%)',
      ];

  return new Chart(canvasCtx, {
    type: 'bar',
    data: {
      labels,
      datasets: ctx.dataList.map((d, i) => ({
        label: ctx.labelFor(i),
        data: classificationMode
          ? [d.stats.avg_confidence !== null ? +(d.stats.avg_confidence * 100).toFixed(1) : 0]
          : [
              d.stats.total_fields,
              d.stats.avg_confidence !== null ? +(d.stats.avg_confidence * 100).toFixed(1) : 0,
            ],
        backgroundColor: ctx.colorFor(i).bg,
        borderColor: ctx.colorFor(i).border,
        borderWidth: 1,
        borderRadius: 6,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { labels: { color: textSecondary } } },
      scales: {
        x: { ticks: { color: textSecondary }, grid: { color: borderColor } },
        y: { ticks: { color: textSecondary }, grid: { color: borderColor }, beginAtZero: true },
      },
    },
  });
}

export function renderArchiveAccuracyChart(canvas: HTMLCanvasElement | undefined, ctx: CompareContext): Chart | null {
  if (!canvas) return null;
  const canvasCtx = canvas.getContext('2d');
  if (!canvasCtx) return null;
  const { textSecondary, borderColor } = chartDefaults();

  const nonRefIndices = getNonReferenceIndices(ctx);
  if (nonRefIndices.length === 0 || ctx.allArchives.length === 0) return null;

  return new Chart(canvasCtx, {
    type: 'bar',
    data: {
      labels: ctx.allArchives,
      datasets: nonRefIndices.map(idx => ({
        label: ctx.labelFor(idx),
        data: ctx.allArchives.map(archive => {
          const score = getArchiveAccuracy(ctx, archive, idx);
          return score !== null ? +(score * 100).toFixed(1) : 0;
        }),
        backgroundColor: ctx.colorFor(idx).bg,
        borderColor: ctx.colorFor(idx).border,
        borderWidth: 1,
        borderRadius: 6,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: textSecondary } },
        tooltip: { callbacks: { title: (items) => ctx.allArchives[items[0].dataIndex] } },
      },
      scales: {
        x: {
          ticks: {
            color: textSecondary,
            callback: (value) => truncateMiddle(ctx.allArchives[value as number], 14),
          },
          grid: { color: borderColor },
        },
        y: { ticks: { color: textSecondary }, grid: { color: borderColor }, beginAtZero: true, max: 100 },
      },
    },
  });
}

export function renderQualityDistributionChart(canvas: HTMLCanvasElement | undefined, ctx: CompareContext): Chart | null {
  if (!canvas) return null;
  const canvasCtx = canvas.getContext('2d');
  if (!canvasCtx) return null;
  const { textSecondary, borderColor } = chartDefaults();

  const nonRefIndices = getNonReferenceIndices(ctx);
  if (nonRefIndices.length === 0) return null;

  const distributions = nonRefIndices.map(idx => getQualityDistribution(ctx, idx));

  return new Chart(canvasCtx, {
    type: 'bar',
    data: {
      labels: nonRefIndices.map(idx => ctx.labelFor(idx)),
      datasets: [
        {
          label: '≥ 90%',
          data: distributions.map(d => d.high),
          backgroundColor: '#24c58ccc',
          borderColor: '#24c58c',
          borderWidth: 1,
        },
        {
          label: '70-89%',
          data: distributions.map(d => d.medium),
          backgroundColor: '#f0b429cc',
          borderColor: '#f0b429',
          borderWidth: 1,
        },
        {
          label: '< 70%',
          data: distributions.map(d => d.low),
          backgroundColor: '#ff6b6bcc',
          borderColor: '#ff6b6b',
          borderWidth: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { labels: { color: textSecondary } } },
      scales: {
        x: { stacked: true, ticks: { color: textSecondary }, grid: { color: borderColor } },
        y: { stacked: true, ticks: { color: textSecondary }, grid: { color: borderColor }, beginAtZero: true },
      },
    },
  });
}

export function renderProblematicFieldsChart(canvas: HTMLCanvasElement | undefined, ctx: CompareContext): Chart | null {
  if (!canvas) return null;
  const canvasCtx = canvas.getContext('2d');
  if (!canvasCtx) return null;
  const { textSecondary, borderColor } = chartDefaults();

  const fields = getMostProblematicFields(ctx, 10);
  if (fields.length === 0) return null;

  return new Chart(canvasCtx, {
    type: 'bar',
    data: {
      labels: fields.map(f => f.field),
      datasets: [{
        label: ctx.translate('COMPARE.SIMILARITY.SCORE'),
        data: fields.map(f => +(f.avgScore * 100).toFixed(1)),
        backgroundColor: fields.map(f => getSimilarityColor(f.avgScore) + 'cc'),
        borderColor: fields.map(f => getSimilarityColor(f.avgScore)),
        borderWidth: 1,
        borderRadius: 6,
      }],
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { title: (items) => fields[items[0].dataIndex].field } },
      },
      scales: {
        x: { ticks: { color: textSecondary }, grid: { color: borderColor }, beginAtZero: true, max: 100 },
        y: {
          ticks: {
            color: textSecondary,
            callback: (value) => truncateMiddle(fields[value as number].field, 22),
          },
          grid: { color: borderColor },
        },
      },
    },
  });
}

export function renderClassAgreementChart(canvas: HTMLCanvasElement | undefined, ctx: CompareContext): Chart | null {
  if (!canvas) return null;
  const canvasCtx = canvas.getContext('2d');
  if (!canvasCtx) return null;
  const { textSecondary, borderColor } = chartDefaults();

  const nonRefIndices = getNonReferenceIndices(ctx);
  if (nonRefIndices.length === 0) return null;

  const rates = nonRefIndices.map(idx => getClassAgreementRate(ctx, idx));

  return new Chart(canvasCtx, {
    type: 'bar',
    data: {
      labels: nonRefIndices.map(idx => ctx.labelFor(idx)),
      datasets: [{
        label: ctx.translate('COMPARE.CHARTS.CLASS_AGREEMENT'),
        data: rates.map(r => r !== null ? +(r * 100).toFixed(1) : 0),
        backgroundColor: nonRefIndices.map(idx => ctx.colorFor(idx).bg),
        borderColor: nonRefIndices.map(idx => ctx.colorFor(idx).border),
        borderWidth: 1,
        borderRadius: 6,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { color: textSecondary }, grid: { color: borderColor } },
        y: { ticks: { color: textSecondary }, grid: { color: borderColor }, beginAtZero: true, max: 100 },
      },
    },
  });
}
